import { ITechnician } from '../utils/entities/interface';
import { get, patch, post } from './https';

// ------------------------------
// Types
// ------------------------------

export interface ILeave {
  _id: string;
  technicianId: string;
  technician: ITechnician;
  startDate: string;
  endDate: string;
  leaveType: 'SICK_LEAVE' | 'CASUAL_LEAVE' | 'PAID_LEAVE';
  status: 'PENDING' | 'APPROVED' | 'REJECTED';
  reason: string;
  createdAt: string;
  updatedAt: string;
}

export interface Pagination {
  totalCount: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface LeaveListResponse {
  status: boolean;
  data: ILeave[];
  count: number;
  pagination: Pagination;
}

export interface LeaveRequestBody {
  technicianId: string;
  startDate: string; // ISO format (yyyy-mm-dd)
  endDate: string;
  leaveType: 'SICK_LEAVE' | 'CASUAL_LEAVE' | 'PAID_LEAVE';
  reason?: string;
}

export interface LeaveSummaryResponse {
  technician: {
    id: string;
    name: string;
    phoneNumber: string;
    profilePhoto: string;
  };
  leaveSummary: {
    total: number;
    approved: number;
    pending: number;
    rejected: number;
  };
  attendanceSummary: {
    totalDays: number;
    present: number;
    absent: number;
    leave: number;
    holiday: number;
  };
  leaveHistory: ILeave[];
  currentMonthAttendance: {
    _id: string;
    date: string;
    type: 'PRESENT' | 'ABSENT' | 'LEAVE' | 'HOLIDAY';
    description?: string;
  }[];
}

/**
 * 📥 Submit a leave request
 */
export const POSTLeaveRequest = async (
  data: LeaveRequestBody,
): Promise<ILeave> => {
  try {
    const response = await post('/api/v1/admin/leave/create', data);

    return response.data.data;
  } catch (error: any) {
    console.error('POSTLeaveRequest failed:', error);
    throw new Error(error?.response?.data?.message || error.message);
  }
};

/**
 * 📃 Get list of leaves with pagination + filters
 */
export const GETLeaves = async ({
  page = 1,
  limit = 25,
  searchQuery = '',
  sortField = 'createdAt',
  sortOrder = 'DESC',
  leaveType,
  startDate,
  endDate,
}: {
  page?: number;
  limit?: number;
  searchQuery?: string;
  sortField?: string;
  sortOrder?: 'ASC' | 'DESC';
  leaveType?: string;
  startDate?: Date | null;
  endDate?: Date | null;
}): Promise<LeaveListResponse> => {
  try {
    let url = `/api/v1/admin/technician/leave/list?page=${page}&limit=${limit}`;
    if (searchQuery) url += `&search=${searchQuery}`;
    if (sortField) url += `&sortby=${sortField}`;
    if (sortOrder) url += `&orderby=${sortOrder.toLowerCase()}`;
    if (leaveType) url += `&leaveType=${leaveType}`;
    if (startDate) url += `&startDate=${startDate.toISOString().split('T')[0]}`;
    if (endDate) url += `&endDate=${endDate.toISOString().split('T')[0]}`;

    const response = await get(url);
    return response.data;
  } catch (error: any) {
    console.error('GETLeaves failed:', error);
    throw new Error(
      error?.response?.data?.message || 'Unable to fetch leave requests',
    );
  }
};

/**
 * ✏️ Update status of a leave request (Approve / Reject)
 */
export const PATCHLeaveStatus = async (
  leaveId: string,
  status: 'APPROVED' | 'REJECTED',
): Promise<ILeave> => {
  try {
    const response = await post(`/api/v1/admin/leave/approval`, {
      status: status,
      leaveId: leaveId,
    });
    return response.data.data;
  } catch (error: any) {
    console.error('PATCHLeaveStatus failed:', error);
    throw new Error(error?.response?.data?.message || error.message);
  }
};
/**
 * 📊 Get technician leave summary + monthly attendance
 */
export const GETTechnicianLeaveDetails = async (
  technicianId: string,
): Promise<LeaveSummaryResponse> => {
  try {
    const response = await get(
      `/api/v1/admin/technician/${technicianId}/leave-details`,
    );
    return response.data.data;
  } catch (error: any) {
    console.error('GETTechnicianLeaveDetails failed:', error);
    throw new Error(
      error?.response?.data?.message ||
        'Unable to fetch technician leave details',
    );
  }
};
